import { RequestBody } from '@/api/react-query.config';
import { convertUTCToTimezone, convertTimezoneToUTC } from '@/utils/timezone';
import { normalizeTeamLogoUrl } from '@/utils/normalizeTeamLogoUrl';

type CreateQuestionRequest = RequestBody<'/api/admin/questions', 'post'>;
type ApiOption = NonNullable<CreateQuestionRequest['options']>[number];

const normalizeAssetUrl = (url?: string | null): string | undefined =>
  url ? normalizeTeamLogoUrl(url) : undefined;

/**
 * Normalize the tiebreaker correct value for the API payload.
 *
 * The form's number input produces an empty string ('') when blank, which the
 * backend schema (number | null) rejects with "Expected union value". The
 * correct value is optional at creation time, so coerce blank/NaN inputs to
 * null instead of sending an empty string.
 */
const normalizeCorrectValue = (value?: number | string | null): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(parsed) ? null : parsed;
};

export type QuestionFormValues = Omit<CreateQuestionRequest, 'options'> & {
  display_order?: number;
  feed_based?: boolean; // Indicates if question was generated via Question Coach
  options: Array<
    ApiOption & {
      option_id?: string;
      is_correct: boolean;
      display_order?: number;
      grading_data?: {
        teamId?: number | string;
        playerId?: number | string;
        playerName?: string;
        playerPosition?: string; // NEW: Player position (QB, WR, etc.)
        teamName?: string; // NEW: Team display name
        teamAbbr?: string; // Team abbreviation (e.g., "KC", "SF")
        threshold?: number;
        comparison?: 'over' | 'under';
        // NEW: Per-option fixture data (supports multi-fixture questions)
        fixtureData?: {
          fixtureId: string;
          homeTeamId: string;
          awayTeamId: string;
          homeTeamName: string;
          awayTeamName: string;
          homeTeamAbbreviation?: string;
          awayTeamAbbreviation?: string;
          scheduledDate: string;
          status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
        };
      };
    }
  >;
  sportcc_match_id?: number | null;
  sportcc_match_data?: {
    homeTeam: { id: number; name: string };
    awayTeam: { id: number; name: string };
    league: string;
    matchDate: string;
    status: string;
  } | null;
  fixture_external_id?: string | null;
  stat_type_code?: string | null;
  team_ids?: string[] | null;
  question_metadata?: {
    dailyDouble?: boolean;
    grading_type?: string;
    grading?: {
      config?: {
        threshold?: number;
        comparison?: 'over' | 'under';
        statType?: string;
        playerId?: string;
        playerName?: string;
        playerPosition?: string;
        teamId?: string | number;
        teamName?: string;
        teamShortcode?: string;
        teamLogoUrl?: string;
        team_id?: number;
        team_name?: string;
        player_count?: number;
        player_id?: number;
        player_name?: string;
      };
    };
    config?: {
      threshold?: number;
      comparison?: 'over' | 'under';
      team_id?: number;
      team_name?: string;
      player_count?: number;
      player_id?: number;
      player_name?: string;
    };
  };
};

export interface QuestionData {
  question_id?: string;
  pickem_id?: string;
  question_text_en?: string;
  question_text_fr?: string;
  question_subtext_en?: string;
  question_subtext_fr?: string;
  question_image_en?: string | null;
  question_image_fr?: string | null;
  start_utc?: string;
  question_type?: string;
  points?: number;
  correct_value?: number | string;
  display_order?: number;
  sportcc_match_id?: number | null;
  sportcc_match_data?: {
    homeTeam: { id: number; name: string };
    awayTeam: { id: number; name: string };
    league: string;
    matchDate: string;
    status: string;
  } | null;
  // Feed-based question metadata (Sportradar)
  feed_based?: boolean;
  provider?: string | null;
  sport_external_id?: string | null;
  league_external_id?: string | null;
  fixture_external_id?: string | null;
  stat_type_code?: string | null;
  margin?: number | null;
  question_metadata?: {
    dailyDouble?: boolean;
    grading?: {
      type: string;
      sportccMatchId: number;
      config?: {
        threshold?: number;
        comparison?: 'over' | 'under';
        statType?: string;
        playerId?: string;
        playerName?: string;
        playerPosition?: string;
        teamId?: string | number;
        teamName?: string;
        teamShortcode?: string;
        teamLogoUrl?: string;
      };
    };
  } | null;
  options?: Array<{
    option_id?: string;
    question_id?: string;
    option_text_en?: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    third_image_en?: string | null;
    third_image_fr?: string | null;
    start_utc?: string;
    is_correct?: boolean | string;
    display_order?: number;
    grading_data?: unknown;
    created_at?: string;
  }>;
}

/**
 * Convert API data to form values
 * @param question - Question data from API
 * @param searchPickemId - Optional pickem ID to pre-fill
 * @param userTimezone - Optional IANA timezone for converting dates to user's timezone
 * @returns Form values with dates in user's timezone (if provided) or UTC
 */
export function apiToFormValues(
  question: QuestionData | undefined,
  searchPickemId?: string,
  userTimezone?: string
): QuestionFormValues {
  let questionType = (question?.question_type as any) || 'team';
  const isTiebreaker = questionType === 'tiebreaker';

  // Convert date: API (UTC) → Form (user timezone or UTC)
  const convertDate = (utcDate: string | undefined): string | undefined => {
    if (!utcDate) return undefined;
    if (userTimezone) {
      return convertUTCToTimezone(utcDate, userTimezone, 'datetime-local');
    }
    // Fallback to original behavior (UTC datetime-local)
    return new Date(utcDate).toISOString().slice(0, 16);
  };

  // Infer team_over_under vs player_over_under when DB stores "over_under" (manual questions)
  const firstOptionGradingData =
    (question?.options?.[0]?.grading_data as {
      playerId?: string;
      playerName?: string;
      playerPosition?: string;
      teamId?: string | number;
      teamName?: string;
      teamShortcode?: string;
      teamLogoUrl?: string;
      threshold?: number;
    }) || {};
  if (questionType === 'over_under') {
    if (firstOptionGradingData.teamId && !firstOptionGradingData.playerId) {
      questionType = 'team_over_under';
    } else if (firstOptionGradingData.playerId) {
      questionType = 'player_over_under';
    }
  }

  // Initialize question_metadata - ensure it exists for all question types (at least as empty object)
  // This ensures consistency and that the field is always present in the form
  let questionMetadata: QuestionFormValues['question_metadata'] = question?.question_metadata || {};

  if (
    (questionType === 'over_under' ||
      questionType === 'team_over_under' ||
      questionType === 'player_over_under') &&
    question
  ) {
    const config = {
      ...(questionMetadata?.grading?.config || {}),
      threshold:
        question.margin ??
        firstOptionGradingData.threshold ??
        questionMetadata?.grading?.config?.threshold ??
        undefined,
      statType: question.stat_type_code ?? questionMetadata?.grading?.config?.statType ?? undefined,
      playerId: firstOptionGradingData.playerId,
      playerName: firstOptionGradingData.playerName,
      playerPosition: firstOptionGradingData.playerPosition,
      teamId: firstOptionGradingData.teamId,
      teamName: firstOptionGradingData.teamName,
      teamShortcode:
        firstOptionGradingData.teamShortcode ?? questionMetadata?.grading?.config?.teamShortcode,
      teamLogoUrl: normalizeAssetUrl(
        firstOptionGradingData.teamLogoUrl ?? questionMetadata?.grading?.config?.teamLogoUrl
      ),
    };

    questionMetadata = {
      ...(questionMetadata || {}),
      grading: {
        ...(questionMetadata?.grading || {}),
        config,
      },
    } as QuestionFormValues['question_metadata'];
  }

  return {
    pickem_id: question?.pickem_id || searchPickemId,
    question_text_en: question?.question_text_en || '',
    question_text_fr: question?.question_text_fr,
    question_subtext_en: question?.question_subtext_en,
    question_subtext_fr: question?.question_subtext_fr,
    question_image_en: question?.question_image_en,
    question_image_fr: question?.question_image_fr,
    start_utc: convertDate(question?.start_utc),
    question_type: questionType,
    points: isTiebreaker ? 0 : question?.points || 1,
    correct_value:
      question?.correct_value !== undefined
        ? typeof question.correct_value === 'string'
          ? parseFloat(question.correct_value)
          : question.correct_value
        : undefined,
    display_order: question?.display_order,
    feed_based: question?.feed_based ?? false,
    sportcc_match_id: question?.sportcc_match_id ?? null,
    sportcc_match_data: question?.sportcc_match_data ?? null,
    fixture_external_id: question?.fixture_external_id ?? null,
    stat_type_code: question?.stat_type_code ?? null,
    question_metadata: questionMetadata,
    options: isTiebreaker
      ? []
      : question?.options && question.options.length > 0
        ? question.options.map((opt, index) => ({
            option_id: opt.option_id,
            option_text_en: opt.option_text_en || '',
            option_text_fr: opt.option_text_fr,
            option_image_en: normalizeAssetUrl(opt.option_image_en),
            option_image_fr: normalizeAssetUrl(opt.option_image_fr),
            third_image_en: normalizeAssetUrl(opt.third_image_en),
            third_image_fr: normalizeAssetUrl(opt.third_image_fr),
            start_utc: convertDate(opt.start_utc),
            is_correct: opt.is_correct === true,
            display_order: opt.display_order ?? index,
            grading_data: (opt.grading_data ??
              undefined) as QuestionFormValues['options'][number]['grading_data'],
          }))
        : [
            {
              option_text_en: '',
              option_text_fr: '',
              option_image_en: undefined,
              option_image_fr: undefined,
              third_image_en: undefined,
              third_image_fr: undefined,
              start_utc: undefined,
              is_correct: false,
              display_order: 0,
            },
            {
              option_text_en: '',
              option_text_fr: '',
              option_image_en: undefined,
              option_image_fr: undefined,
              third_image_en: undefined,
              third_image_fr: undefined,
              start_utc: undefined,
              is_correct: false,
              display_order: 1,
            },
          ],
  };
}

/**
 * Convert form values to create payload
 * @param values - Form values
 * @param userTimezone - Optional IANA timezone for converting dates from user's timezone to UTC
 * @returns Create request payload with dates in UTC
 */
export function formValuesToCreatePayload(
  values: QuestionFormValues,
  userTimezone?: string
): CreateQuestionRequest {
  const isTiebreaker = values.question_type === 'tiebreaker';

  // Convert date: Form (user timezone) → API (UTC)
  const convertDate = (localDate: string | undefined): string | undefined => {
    if (!localDate) return undefined;
    if (userTimezone) {
      return convertTimezoneToUTC(localDate, userTimezone);
    }
    // Fallback to original behavior (pass through)
    return localDate;
  };

  const formattedOptions = isTiebreaker
    ? undefined
    : values.options.map((opt, index) => ({
        option_id: opt.option_id,
        option_text_en: opt.option_text_en,
        option_text_fr: opt.option_text_fr,
        option_image_en: opt.option_image_en,
        option_image_fr: opt.option_image_fr,
        third_image_en: opt.third_image_en,
        third_image_fr: opt.third_image_fr,
        start_utc: convertDate(opt.start_utc),
        is_correct: opt.is_correct,
        display_order: index,
        grading_data: opt.grading_data,
      }));

  const payload: CreateQuestionRequest = {
    pickem_id: values.pickem_id,
    question_text_en: values.question_text_en,
    question_text_fr: values.question_text_fr,
    question_subtext_en: values.question_subtext_en,
    question_subtext_fr: values.question_subtext_fr,
    question_image_en: values.question_image_en,
    question_image_fr: values.question_image_fr,
    start_utc: convertDate(values.start_utc),
    question_type: values.question_type,
    points: values.points,
    correct_value: normalizeCorrectValue(values.correct_value),
    options: formattedOptions,
  };

  // Only include SportCC fields if match is selected
  if (values.sportcc_match_id) {
    payload.sportcc_match_id = values.sportcc_match_id;
    payload.sportcc_match_data = values.sportcc_match_data;
  }

  // Include fixture external ID, stat type code, and team IDs for feed-based question detection
  if (values.fixture_external_id) {
    (payload as any).fixture_external_id = values.fixture_external_id;
  }
  if (values.stat_type_code) {
    (payload as any).stat_type_code = values.stat_type_code;
  }
  if (values.team_ids) {
    (payload as any).team_ids = values.team_ids;
  }

  // Include question metadata - ensure it's always initialized (at least as empty object)
  // Backend expects questionMetadata to exist for consistency across question types
  let questionMetadata = values.question_metadata || {};

  // For over/under questions, ensure stat_type_code is synced into question_metadata.grading.config.statType
  // so the frontend extractStatData() can find it for display
  const isOverUnder =
    (values.question_type as string) === 'player_over_under' ||
    (values.question_type as string) === 'team_over_under' ||
    values.question_type === 'over_under';
  if (isOverUnder && values.stat_type_code) {
    questionMetadata = {
      ...questionMetadata,
      grading: {
        ...(questionMetadata.grading || {}),
        config: {
          ...(questionMetadata.grading?.config || {}),
          statType: values.stat_type_code,
        },
      },
    } as typeof questionMetadata;
  }

  // Always include question_metadata (even if empty) for consistency
  payload.question_metadata = questionMetadata;

  return payload;
}

/**
 * Convert form values to update payload
 * @param questionId - Question ID
 * @param values - Form values
 * @param userTimezone - Optional IANA timezone for converting dates from user's timezone to UTC
 * @returns Update request payload with dates in UTC
 */
export function formValuesToUpdatePayload(
  questionId: string,
  values: QuestionFormValues,
  userTimezone?: string
) {
  const questionType = values.question_type || 'team';
  const isTiebreaker = questionType === 'tiebreaker';

  if (!values.pickem_id) {
    throw new Error('pickem_id is required for update');
  }

  // Convert date: Form (user timezone) → API (UTC)
  const convertDate = (localDate: string | undefined): string | undefined => {
    if (!localDate) return undefined;
    if (userTimezone) {
      return convertTimezoneToUTC(localDate, userTimezone);
    }
    // Fallback to original behavior (pass through)
    return localDate;
  };

  // Backend expects the same format as POST request (CreateQuestionBodySchema)
  const formattedOptions = isTiebreaker
    ? undefined
    : values.options.map((opt, index) => ({
        option_id: opt.option_id,
        option_text_en: opt.option_text_en,
        option_text_fr: opt.option_text_fr,
        option_image_en: opt.option_image_en,
        option_image_fr: opt.option_image_fr,
        third_image_en: opt.third_image_en,
        third_image_fr: opt.third_image_fr,
        start_utc: convertDate(opt.start_utc),
        is_correct: opt.is_correct,
        display_order: index,
        grading_data: opt.grading_data,
      }));

  const payload: any = {
    questionId, // Required for API call routing
    pickemId: values.pickem_id, // Keep for navigation after success
    data: {
      pickem_id: values.pickem_id,
      question_text_en: values.question_text_en,
      question_text_fr: values.question_text_fr,
      question_subtext_en: values.question_subtext_en,
      question_subtext_fr: values.question_subtext_fr,
      question_image_en: values.question_image_en,
      question_image_fr: values.question_image_fr,
      start_utc: convertDate(values.start_utc),
      question_type: values.question_type,
      points: values.points,
      correct_value: normalizeCorrectValue(values.correct_value),
      options: formattedOptions,
    },
  };

  // Only include SportCC fields if match is selected
  if (values.sportcc_match_id) {
    payload.data.sportcc_match_id = values.sportcc_match_id;
    payload.data.sportcc_match_data = values.sportcc_match_data;
  }

  // Include fixture external ID, stat type code, and team IDs for feed-based question detection
  if (values.fixture_external_id) {
    (payload.data as any).fixture_external_id = values.fixture_external_id;
  }
  if (values.stat_type_code) {
    (payload.data as any).stat_type_code = values.stat_type_code;
  }
  if (values.team_ids) {
    (payload.data as any).team_ids = values.team_ids;
  }

  // Include question metadata - ensure it's always initialized (at least as empty object)
  // Backend expects questionMetadata to exist for consistency across question types
  let questionMetadata = values.question_metadata || {};

  // For over/under questions, ensure stat_type_code is synced into question_metadata.grading.config.statType
  // so the frontend extractStatData() can find it for display
  const isOverUnder =
    (questionType as string) === 'player_over_under' ||
    (questionType as string) === 'team_over_under' ||
    questionType === 'over_under';
  if (isOverUnder && values.stat_type_code) {
    questionMetadata = {
      ...questionMetadata,
      grading: {
        ...(questionMetadata.grading || {}),
        config: {
          ...(questionMetadata.grading?.config || {}),
          statType: values.stat_type_code,
        },
      },
    } as typeof questionMetadata;
  }

  // Always include question_metadata (even if empty) for consistency
  payload.data.question_metadata = questionMetadata;

  return payload;
}
